import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../../services/api';
import { MeetingSummary, Medication, DiagnosisCode, PDFResponse, MedicationType, User, ClinicUserApiResponse } from '../../types/api';
import { PDFViewerModal } from './PDFViewerModal';
import { MedicationSection } from '../medications/MedicationSection';
import { POPUP_STYLES } from '../../constants/popupStyles';

// Function to expand textarea as user types
const expandTextarea = (textarea: HTMLTextAreaElement) => {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Set height based on scrollHeight
    textarea.style.height = Math.max(textarea.scrollHeight, 80) + 'px';
};

interface PSYSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    meetingId?: number;
    clinicId?: number;
    token: string;
    medicationTypes: MedicationType[];
    diagnosisCodes: DiagnosisCode[];
    onSuccess?: () => void;
    /** Called after a successful in-place save (Update/Approve) so the opener can
     *  refresh cached meeting data. Unlike onSuccess, it does not close/navigate. */
    onSaved?: () => void;
    onStatusMessage?: (message: { type: 'success' | 'error' | 'loading'; text: string } | null) => void;
    taskIssue?: string;
    user?: User | ClinicUserApiResponse;
}

export const PSYSummaryModal: React.FC<PSYSummaryModalProps> = ({
    isOpen,
    onClose,
    meetingId,
    clinicId,
    token,
    medicationTypes,
    diagnosisCodes: propsdiagnosisCodes,
    onSuccess,
    onSaved,
    onStatusMessage,
    taskIssue,
    user,
}) => {
    // State
    const [activeTab, setActiveTab] = useState<'summary' | 'notes'>('summary');
    const [summary, setSummary] = useState<MeetingSummary | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showExitWarning, setShowExitWarning] = useState(false);
    const [showMedicationWarning, setShowMedicationWarning] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        summary_and_discussion: '',
        status: '',
        recommendations: '',
        diagnosis: [] as string[],
        diagnosis_text: '',
        demographics: '',
        hpi: '',
        background: '',
        referral_reason: '',
    });

    // Medications state
    const [medications, setMedications] = useState<Medication[]>([]);
    const [medicationsEnabled, setMedicationsEnabled] = useState(true);
    const medicationError: string | null = null;

    // Textarea refs
    const summaryTextareaRef = useRef<HTMLTextAreaElement>(null);
    const recommendationsTextareaRef = useRef<HTMLTextAreaElement>(null);

    // Editable form field textarea refs
    const demographicsRef = useRef<HTMLTextAreaElement>(null);
    const referralReasonRef = useRef<HTMLTextAreaElement>(null);
    const backgroundRef = useRef<HTMLTextAreaElement>(null);
    const hpiRef = useRef<HTMLTextAreaElement>(null);
    const summaryDiscussionRef = useRef<HTMLTextAreaElement>(null);
    const statusRef = useRef<HTMLTextAreaElement>(null);
    const recommendationsRef = useRef<HTMLTextAreaElement>(null);

    // Auto-expand textareas when values change or when switching to Summary tab
    useEffect(() => {
        if (activeTab === 'summary') {
            const refs = [demographicsRef, referralReasonRef, backgroundRef, hpiRef, summaryDiscussionRef, statusRef, recommendationsRef];
            refs.forEach(ref => {
                if (ref.current) {
                    expandTextarea(ref.current);
                }
            });
        }
    }, [formData.demographics, formData.referral_reason, formData.background, formData.hpi, formData.summary_and_discussion, formData.status, formData.recommendations, activeTab, summary]);

    // Auto-expand Notes tab textareas on summary load
    useEffect(() => {
        if (summary && activeTab === 'notes') {
            if (summaryTextareaRef.current) {
                expandTextarea(summaryTextareaRef.current);
            }
            if (recommendationsTextareaRef.current) {
                expandTextarea(recommendationsTextareaRef.current);
            }
        }
    }, [summary, activeTab]);


    // Diagnosis state
    const diagnosisCodes = propsdiagnosisCodes;
    const [diagnosisSearch, setDiagnosisSearch] = useState('');
    const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);
    // Per-diagnosis note editor: which diagnosis's note is open, plus the draft.
    // Notes are stored inside formData.diagnosis_text as a JSON map keyed by code.
    const [editingNoteCode, setEditingNoteCode] = useState<string | null>(null);
    const [noteDraft, setNoteDraft] = useState('');

    // Action states
    const [isSaving, setIsSaving] = useState(false);
    const [pdfData, setPdfData] = useState<PDFResponse | null>(null);
    const [showPDFViewer, setShowPDFViewer] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // Helper function to map API medication format to component format
    const mapApiMedicationToComponentFormat = (apiMed: any, medTypes: MedicationType[]): Medication | null => {
        // Try to get the medication name from medication_type_id
        let medName = apiMed.name || '';

        if (!medName && apiMed.medication_type_id && medTypes.length > 0) {
            const medType = medTypes.find(mt => mt.id === apiMed.medication_type_id);
            if (medType) {
                medName = medType.name;
            }
        }

        // Skip medications without a valid name
        if (!medName || medName.trim() === '') {
            return null;
        }

        return {
            id: apiMed.id,
            created_at: apiMed.created_at,
            updated_at: apiMed.updated_at,
            created_by: apiMed.created_by,
            patient_id: apiMed.patient_id,
            meeting_id: apiMed.meeting_id,
            medication_type_id: apiMed.medication_type_id,
            name: medName,
            dosage: apiMed.dosage || '0',
            unit: apiMed.unit || '',
            per_day: apiMed.per_day || '',
            how_many_days: apiMed.how_many_days || '',
            amount_per_take: apiMed.amount_per_take || 0,
            amount: Number(apiMed.amount) || 0,
            amount_per: (apiMed.amount_per || 'Day') as 'Day' | 'Week' | 'Month',
            duration: Number(apiMed.duration) || 0,
            number_of_packages: apiMed.number_of_packages || 0,
            likelihood_of_med_in_predictix: apiMed.likelihood_of_med_in_predictix || '',
            start_date: apiMed.start_date || null,
            comments: apiMed.comments || '',
        };
    };

    // Load meeting summary
    const loadMeetingSummary = useCallback(async () => {
        if (!meetingId) return;

        setIsLoading(true);
        setMedications([]);
        setError(null);

        try {
            const data = await apiService.getMeetingSummary({ token, meeting_id: meetingId });
            console.log('[PSYSummaryModal] summary loaded - summary_sent:', data.summary_sent, 'prescription_updated:', data.prescription_updated, 'is_maccabi:', data.is_maccabi);
            setSummary(data);

            // Initialize form data
            setFormData({
                summary_and_discussion: data.summary_and_discussion_text || '',
                status: data.status_text || '',
                recommendations: data.recommendations_text || '',
                diagnosis: data.diagnosis ? data.diagnosis.split(',').map((code: string) => code.trim()).filter((code: string) => code) : [],
                diagnosis_text: data.diagnosis_text || '',
                demographics: data.personal_demographics || '',
                hpi: data.hpi || '',
                background: data.background || '',
                referral_reason: data.referral_reason || '',
            });

            setHasUnsavedChanges(false);
        } catch (err: any) {
            setError(err.message || 'Failed to load meeting summary');
        } finally {
            setIsLoading(false);
        }
    }, [meetingId, token]);

    useEffect(() => {
        if (isOpen && meetingId) {
            loadMeetingSummary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, meetingId]);

    // Map medications when medicationTypes are loaded
    useEffect(() => {
        if (summary && medicationTypes.length > 0) {
            const mappedMedications = (summary.medications || [])
                .map(med => mapApiMedicationToComponentFormat(med, medicationTypes))
                .filter((med): med is Medication => med !== null);

            setMedications(mappedMedications);
        }
    }, [summary, medicationTypes]);

    // Check if data has changed
    const checkIfChanged = (): boolean => {
        if (!summary) return false;

        return (
            formData.recommendations !== (summary.recommendations_text || '') ||
            formData.status !== (summary.status_text || '') ||
            formData.summary_and_discussion !== (summary.summary_and_discussion_text || '') ||
            formData.demographics !== (summary.personal_demographics || '') ||
            formData.referral_reason !== (summary.referral_reason || '') ||
            formData.background !== (summary.background || '') ||
            formData.hpi !== (summary.hpi || '') ||
            formData.diagnosis.join(',') !== (summary.diagnosis || '') ||
            formData.diagnosis_text !== (summary.diagnosis_text || '') ||
            JSON.stringify(medications) !== JSON.stringify(summary.medications || [])
        );
    };

    // Handle form changes
    const handleFormChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasUnsavedChanges(true);
    };


    // Diagnosis handling
    const handleAddDiagnosis = (code: string) => {
        if (!formData.diagnosis.includes(code)) {
            handleFormChange('diagnosis', [...formData.diagnosis, code]);
        }
        setDiagnosisSearch('');
        setShowDiagnosisDropdown(false);
    };

    // Parse the diagnosis_text string into a per-code note map. Legacy/invalid
    // (non-JSON) values are treated as no notes.
    const parseDiagnosisTextMap = (raw: string): Record<string, string> => {
        try {
            const parsed = JSON.parse(raw || '{}');
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed as Record<string, string>;
            }
        } catch { /* fall through */ }
        return {};
    };

    // Serialize a note map back into diagnosis_text JSON, in the order of the
    // given diagnosis codes. Empty notes are dropped; returns "" when none remain.
    const serializeNotes = (codes: string[], map: Record<string, string>): string => {
        const ordered: Record<string, string> = {};
        for (const code of codes) {
            const text = (map[code] || '').trim();
            if (text) ordered[code] = text;
        }
        return Object.keys(ordered).length > 0 ? JSON.stringify(ordered) : '';
    };

    const handleRemoveDiagnosis = (code: string) => {
        const newDiagnosis = formData.diagnosis.filter(d => d !== code);
        const { [code]: _removed, ...restMap } = parseDiagnosisTextMap(formData.diagnosis_text);
        setFormData(prev => ({
            ...prev,
            diagnosis: newDiagnosis,
            diagnosis_text: serializeNotes(newDiagnosis, restMap),
        }));
        setHasUnsavedChanges(true);
        if (editingNoteCode === code) {
            setEditingNoteCode(null);
            setNoteDraft('');
        }
    };

    // Open the inline note editor, seeding the draft with the existing note.
    const handleStartEditNote = (code: string) => {
        setNoteDraft(parseDiagnosisTextMap(formData.diagnosis_text)[code] || '');
        setEditingNoteCode(code);
    };

    // Commit the draft into the note map (dropping it when empty) and close the editor.
    const handleSaveNote = (code: string) => {
        const text = noteDraft.trim();
        const map = parseDiagnosisTextMap(formData.diagnosis_text);
        if (text) map[code] = text; else delete map[code];
        handleFormChange('diagnosis_text', serializeNotes(formData.diagnosis, map));
        setEditingNoteCode(null);
        setNoteDraft('');
    };

    const handleCancelEditNote = () => {
        setEditingNoteCode(null);
        setNoteDraft('');
    };

    // Clear a saved note without removing the diagnosis itself.
    const handleRemoveNote = (code: string) => {
        const { [code]: _removed, ...restMap } = parseDiagnosisTextMap(formData.diagnosis_text);
        handleFormChange('diagnosis_text', serializeNotes(formData.diagnosis, restMap));
    };

    const getFilteredDiagnosisCodes = () => {
        if (!diagnosisSearch) return diagnosisCodes.slice(0, 50);
        const search = diagnosisSearch.toLowerCase();
        return diagnosisCodes
            .filter(d =>
                d.code.toLowerCase().includes(search) ||
                d.Diagnosis.toLowerCase().includes(search)
            )
            .slice(0, 50);
    };


    // Build update payload
    const buildUpdatePayload = (includeExtraParams: boolean = false) => {
        // Transform medications to API format
        const apiMedications = medications.map(med => ({
            id: med.id || 0,
            patient_id: med.patient_id || 0,
            meeting_id: med.meeting_id || meetingId || 0,
            medication_type_id: med.medication_type_id || 0,
            dosage: med.dosage,                    // Already string
            unit: med.unit,
            per_day: med.per_day || '',
            how_many_days: med.how_many_days || '',
            amount_per_take: med.amount_per_take || 0,
            amount: med.amount,                    // Already number
            amount_per: med.amount_per,
            duration: med.duration,                // Already number
            number_of_packages: med.number_of_packages || 0,
            likelihood_of_med_in_predictix: med.likelihood_of_med_in_predictix || '',
            start_date: med.start_date || null,
            comments: med.comments || '',
        }));

        const payload: any = {
            token,
            meeting_id: meetingId!,
            diagnosis: formData.diagnosis.join(','),
            diagnosis_text: formData.diagnosis_text,
            medications: apiMedications,
            recommandations: formData.recommendations,
            summary_and_discusion: formData.summary_and_discussion,
            status: formData.status,
            clinic_id: summary?.clinic_id || clinicId || 0,
        };

        // Add clinic-specific fields if clinic_id === 24
        if (summary?.clinic_id === 24) {
            payload.demographics = formData.demographics;
            payload.hpi = formData.hpi;
            payload.background = formData.background;
            payload.referral_reason = formData.referral_reason;
        }

        payload.prescription_required = summary?.is_maccabi && medications.length > 0;

        return payload;
    };

    // Update summary
    const handleUpdate = async () => {
        if (!meetingId) return;

        setIsSaving(true);
        if (onStatusMessage) {
            onStatusMessage({ type: 'loading', text: 'Saving changes...' });
        }
        try {
            await apiService.updateMeetingSummary(buildUpdatePayload());
            setHasUnsavedChanges(false);
            if (onSaved) onSaved();
            if (onStatusMessage) {
                onStatusMessage({ type: 'success', text: 'Changes saved successfully' });
                setTimeout(() => onStatusMessage(null), 3000);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to update summary');
            if (onStatusMessage) {
                onStatusMessage({ type: 'error', text: err.message || 'Failed to save changes' });
                setTimeout(() => onStatusMessage(null), 5000);
            }
        } finally {
            setIsSaving(false);
        }
    };

    // View PDF
    const handleViewPDF = async () => {
        if (!meetingId) return;

        setIsGeneratingPDF(true);
        try {
            // Save changes if any
            if (checkIfChanged()) {
                await apiService.updateMeetingSummary(buildUpdatePayload());
            }

            // Get PDF data
            const pdfResponse = await apiService.getMeetingSummaryPDF({ token, meeting_id: meetingId });
            setPdfData(pdfResponse);
            setShowPDFViewer(true);
        } catch (err: any) {
            setError(err.message || 'Failed to generate PDF');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    // Send summary
    const handleSend = async () => {
        if (!meetingId) return;

        // Validate medications if toggle is on
        if (medicationsEnabled && medications.length === 0 && clinicId !== 24) {
            setShowMedicationWarning(true);
            return;
        }

        setIsSaving(true);
        try {
            await apiService.sendSummaryPDF(buildUpdatePayload());
            setHasUnsavedChanges(false);
            if (onSuccess) onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to send summary');
        } finally {
            setIsSaving(false);
        }
    };

    // Approve summary
    const handleApprove = async () => {
        if (!meetingId) return;

        // Validate medications if toggle is on
        if (medicationsEnabled && medications.length === 0) {
            setShowMedicationWarning(true);
            return;
        }

        setIsSaving(true);
        try {
            await apiService.approvePsySummary(buildUpdatePayload());
            setHasUnsavedChanges(false);
            if (onSaved) onSaved();
        } catch (err: any) {
            setError(err.message || 'Failed to approve summary');
        } finally {
            setIsSaving(false);
        }
    };

    // Add prescription to Clicks (Maccabi only)
    const handleAddPrescription = async () => {
        if (!meetingId) return;

        setIsSaving(true);
        try {
            await apiService.addPrescriptionToClicks(buildUpdatePayload());
            setHasUnsavedChanges(false);
            if (onSuccess) onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to add prescription to Clicks');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle close
    const handleClose = () => {
        if (checkIfChanged() && hasUnsavedChanges) {
            setShowExitWarning(true);
        } else {
            onClose();
        }
    };

    // Copy to clipboard
    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    if (!isOpen) return null;

    // Safely evaluate API boolean fields that may arrive as string "true"/"false"/"1"/"0"
    const isApiTrue = (val: boolean | string | number | undefined | null): boolean =>
        val === true || val === 1 || val === '1' || val === 'true';

    // Format date
    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-GB');
    };

    return (
        <>
            {/* Main Modal */}
            <div className="fixed inset-0 bg-black/50 z-[1000] overflow-y-auto flex items-center justify-center p-6">
                <div className="w-full max-w-7xl bg-[#B8DDD0] rounded-3xl overflow-y-hidden flex flex-col h-[90vh] shadow-2xl">
                    {/* Header */}
                    <div className="bg-[#B8DDD0] px-6 py-2">
                        <div className="max-w-6xl mx-auto">
                            {/* Top bar with close button and tabs */}
                            <div className="flex items-center justify-between">
                                {/* Tabs (Left) */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setActiveTab('summary')}
                                        className={`px-4 py-1.5 rounded transition-colors text-sm font-medium ${activeTab === 'summary'
                                            ? 'bg-white text-gray-900'
                                            : 'bg-transparent text-gray-700 hover:bg-[rgba(255,255,255,0.5)]'
                                            }`}
                                    >
                                        Summary
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('notes')}
                                        className={`px-4 py-1.5 rounded transition-colors text-sm font-medium ${activeTab === 'notes'
                                            ? 'bg-white text-gray-900'
                                            : 'bg-transparent text-gray-700 hover:bg-[rgba(255,255,255,0.5)]'
                                            }`}
                                    >
                                        Meeting Notes
                                    </button>
                                </div>

                                {/* Close Button (Right) */}
                                <button
                                    onClick={handleClose}
                                    className="w-8 h-8 flex items-center justify-center text-gray-700 hover:text-gray-900 text-xl font-light"
                                    disabled={isSaving}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="flex-1 max-w-6xl mx-auto w-full px-6 py-4 overflow-y-auto bg-[#EBF5F5]">
                        {/* Meeting Info Container - Only show in Summary tab */}
                        {summary && activeTab === 'summary' && (
                            <div className="bg-[#EBF5F5] rounded-lg p-6 mb-0">
                                <div className="flex items-start justify-between mb-2">
                                    {/* Left: Patient Info */}
                                    <div className="text-xs text-gray-700 space-y-0.5 text-left">
                                        <div>{summary.name}</div>
                                        <div>{formatDate(summary.start)}</div>
                                        <div>ת.ז: {summary.patient_tz}</div>
                                    </div>

                                    {/* Center: Title */}
                                    <div className="flex-1 text-center">
                                        <h2 className={POPUP_STYLES.title}>
                                            {summary.meetingtype_id === 2 ? 'סיכום פסיכיאטרי' : 'סיכום מעקב פסיכיאטרי'}
                                        </h2>
                                    </div>

                                    {/* Right: Doctor Copy Text */}
                                    {summary.doctor_copy_text && (
                                        <div className="text-xs text-gray-700 text-right max-w-[200px]" dir="rtl">
                                            {summary.doctor_copy_text}
                                        </div>
                                    )}
                                </div>

                                {/* Transcript Warning - Centered */}
                                {summary.empty_transcript && (
                                    <div className="text-center mt-2">
                                        <span className="text-red-600 text-sm font-medium">Transcript not available</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {isLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-700"></div>
                            </div>
                        ) : error ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                                {error}
                            </div>
                        ) : activeTab === 'summary' ? (
                            renderSummaryTab()
                        ) : (
                            renderNotesTab()
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-[#B5D9D4] px-6 py-3 border-t border-gray-300" dir="ltr">
                        <div className="flex items-center justify-between max-w-6xl mx-auto">
                            <div className="flex items-center gap-4">
                                {/* PDF Icon */}
                                <button
                                    onClick={handleViewPDF}
                                    disabled={isGeneratingPDF}
                                    className="text-[#3a7380] hover:text-[#2a5360] disabled:opacity-50"
                                    title="Show PDF"
                                >
                                    {isGeneratingPDF ? (
                                        <svg className="w-6 h-6 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>

                            <div className="flex gap-3">
                                {/* Save Button */}
                                <button
                                    onClick={handleUpdate}
                                    disabled={isSaving || !!medicationError}
                                    className={POPUP_STYLES.button}
                                >
                                    Save
                                </button>

                                {/* Approve Button (Maccabi only) */}
                                {summary?.is_maccabi && (
                                    <button
                                        onClick={handleApprove}
                                        disabled={isSaving || !!medicationError}
                                        className={POPUP_STYLES.button}
                                    >
                                        {isApiTrue(summary?.summary_approved) ? 'Summary Approved' : 'Approve Summary'}
                                    </button>
                                )}

                                {/* Add Prescription to Clicks Button (Maccabi + medications only) */}
                                {summary?.is_maccabi && medications.length > 0 && !user?.hide_update_prescription && !isApiTrue(summary?.prescription_updated) && (
                                    <button
                                        onClick={handleAddPrescription}
                                        disabled={isSaving || !!medicationError}
                                        className={POPUP_STYLES.button}
                                    >
                                        Add Prescription to Clicks
                                    </button>
                                )}

                                {/* Send/Update Button */}
                                {!(summary?.is_maccabi && isApiTrue(summary?.summary_sent)) && (
                                    <button
                                        onClick={handleSend}
                                        disabled={isSaving || !!medicationError}
                                        className={POPUP_STYLES.button}
                                    >
                                        {summary?.is_maccabi ? 'Update In Clicks' : 'Send'}
                                    </button>
                                )}

                                {/* Cancel Button */}
                                <button
                                    onClick={handleClose}
                                    disabled={isSaving}
                                    className={POPUP_STYLES.button}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Saving Spinner Overlay */}
            {isSaving && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[1200]">
                    <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3 shadow-lg">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3a7380]"></div>
                        <p className="text-gray-700 font-medium">Saving...</p>
                    </div>
                </div>
            )}

            {/* Exit Warning Modal */}
            {showExitWarning && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]">
                    <div className="bg-[#B8DDD0] rounded-3xl p-8 max-w-md relative shadow-2xl">
                        {/* Close button */}
                        <button
                            onClick={() => setShowExitWarning(false)}
                            className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center text-gray-700 hover:text-gray-900 text-xl font-light"
                        >
                            ✕
                        </button>

                        {/* Title */}
                        <h3 className={`${POPUP_STYLES.title} text-center mb-8 mt-6`}>
                            Do you want to save the changes?
                        </h3>

                        {/* Buttons */}
                        <div className="flex justify-center gap-6">
                            <button
                                onClick={async () => {
                                    await handleUpdate();
                                    setShowExitWarning(false);
                                    onClose();
                                }}
                                className={POPUP_STYLES.button}
                            >
                                Save
                            </button>
                            <button
                                onClick={() => {
                                    setShowExitWarning(false);
                                    onClose();
                                }}
                                className={POPUP_STYLES.button}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Medication Warning Modal */}
            {showMedicationWarning && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]">
                    <div className="bg-[#B8DDD0] rounded-3xl p-8 max-w-md relative shadow-2xl">
                        {/* Close button */}
                        <button
                            onClick={() => setShowMedicationWarning(false)}
                            className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center text-gray-700 hover:text-gray-900 text-xl font-light"
                        >
                            ✕
                        </button>

                        {/* Message */}
                        <div className="text-center text-gray-700 mb-8 mt-6 px-4">
                            Mark recommended medications or select the button if none.
                        </div>

                        {/* Button */}
                        <div className="flex justify-center">
                            <button
                                onClick={() => setShowMedicationWarning(false)}
                                className={POPUP_STYLES.button}
                            >
                                Ok
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PDF Viewer Modal */}
            <PDFViewerModal
                isOpen={showPDFViewer}
                onClose={() => setShowPDFViewer(false)}
                pdfData={pdfData}
            />
        </>
    );

    // Render Summary Tab
    function renderSummaryTab() {
        if (!summary) return null;

        const diagnosisTextMap = parseDiagnosisTextMap(formData.diagnosis_text);

        return (
            <div className="space-y-0">

                {/* Clinic-Specific Sections (clinic_id === 24) */}
                {summary.clinic_id === 24 && (
                    <div className="p-4 space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1" dir="rtl">
                                פרטים דמוגרפיים
                            </label>
                            <textarea
                                ref={demographicsRef}
                                value={formData.demographics}
                                onChange={(e) => {
                                    handleFormChange('demographics', e.target.value);
                                    expandTextarea(e.target as HTMLTextAreaElement);
                                }}
                                className="w-full px-3 py-2 rounded border border-[#B4D9D4] bg-[#B5D9D4] text-[#366A75] focus:outline-none focus:ring-2 focus:ring-[#366A75] text-sm overflow-y-hidden"
                                style={{ resize: 'none', height: '80px' }}
                                dir="rtl"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1" dir="rtl">
                                סיבת הפנייה
                            </label>
                            <textarea
                                ref={referralReasonRef}
                                value={formData.referral_reason}
                                onChange={(e) => {
                                    handleFormChange('referral_reason', e.target.value);
                                    expandTextarea(e.target as HTMLTextAreaElement);
                                }}
                                className="w-full px-3 py-2 rounded border border-[#B4D9D4] bg-[#B5D9D4] text-[#366A75] focus:outline-none focus:ring-2 focus:ring-[#366A75] text-sm overflow-y-hidden"
                                style={{ resize: 'none', height: '80px' }}
                                dir="rtl"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1" dir="rtl">
                                רקע
                            </label>
                            <textarea
                                ref={backgroundRef}
                                value={formData.background}
                                onChange={(e) => {
                                    handleFormChange('background', e.target.value);
                                    expandTextarea(e.target as HTMLTextAreaElement);
                                }}
                                className="w-full px-3 py-2 rounded border border-[#B4D9D4] bg-[#B5D9D4] text-[#366A75] focus:outline-none focus:ring-2 focus:ring-[#366A75] text-sm overflow-y-hidden"
                                style={{ resize: 'none', height: '80px' }}
                                dir="rtl"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1" dir="rtl">
                                תיאור מחלה נוכחית
                            </label>
                            <textarea
                                ref={hpiRef}
                                value={formData.hpi}
                                onChange={(e) => {
                                    handleFormChange('hpi', e.target.value);
                                    expandTextarea(e.target as HTMLTextAreaElement);
                                }}
                                className="w-full px-3 py-2 rounded border border-[#B4D9D4] bg-[#B5D9D4] text-[#366A75] focus:outline-none focus:ring-2 focus:ring-[#366A75] text-sm overflow-y-hidden"
                                style={{ resize: 'none', height: '80px' }}
                                dir="rtl"
                            />
                        </div>
                    </div>
                )}

                {/* Copy Icon - Outside sections */}
                <div className="flex justify-end mb-2">
                    <button
                        onClick={() => handleCopyToClipboard(
                            (summary.doctor_copy_text || '') + '\n' +
                            formData.summary_and_discussion + '\n' +
                            'סטטוס פסיכאטרי: \n' + formData.status
                        )}
                        className="text-black hover:text-gray-700"
                        title="Copy to clipboard"
                    >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                        </svg>
                    </button>
                </div>

                {/* Summary and Discussion */}
                <div className="p-4">
                    <label className="block text-xs font-medium text-gray-700 mb-2" dir="rtl">
                        סיכום ודיון
                    </label>
                    <textarea
                        ref={summaryDiscussionRef}
                        value={formData.summary_and_discussion}
                        onChange={(e) => {
                            handleFormChange('summary_and_discussion', e.target.value);
                            expandTextarea(e.target as HTMLTextAreaElement);
                        }}
                        className="w-full px-3 py-2 rounded border border-[#B4D9D4] bg-[#B5D9D4] text-[#366A75] focus:outline-none focus:ring-2 focus:ring-[#366A75] text-sm overflow-y-hidden"
                        style={{ resize: 'none', height: '80px' }}
                        dir="rtl"
                    />
                </div>

                {/* Diagnosis */}
                <div className="p-4">
                    <label className="block text-xs font-medium text-gray-700 mb-2" dir="rtl">
                        אבחנה
                    </label>

                    {/* Selected diagnoses - one per row, with optional per-diagnosis note */}
                    {formData.diagnosis.length > 0 && (
                        <div className="flex flex-col gap-2 mb-2">
                            {formData.diagnosis.map((code) => {
                                const diagnosisItem = diagnosisCodes.find(d => d.code === code);
                                const note = diagnosisTextMap[code] || '';
                                const isEditing = editingNoteCode === code;
                                return (
                                    <div key={code} className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                            {/* Chip: code + name, with optional connected note segment */}
                                            <span className="inline-flex items-stretch text-xs rounded-lg overflow-hidden border border-[#B4D9D4] flex-shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => handleStartEditNote(code)}
                                                    title="Click to add or edit a note"
                                                    className="px-2 py-1 bg-[#B5D9D4] text-gray-900 text-left hover:bg-[#a6cfc9] transition-colors"
                                                >
                                                    {code}
                                                    {diagnosisItem && ` - ${diagnosisItem.Diagnosis}`}
                                                </button>
                                                {note && !isEditing && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#E0F1EE] border-l border-[#B4D9D4] text-gray-700">
                                                        {note}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveNote(code)}
                                                            title="Remove note"
                                                            className="ml-1 text-gray-500 hover:text-red-600"
                                                        >
                                                            ✕
                                                        </button>
                                                    </span>
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveDiagnosis(code)}
                                                title="Remove diagnosis"
                                                className="text-gray-500 hover:text-red-600 flex-shrink-0"
                                            >
                                                ✕
                                            </button>
                                        </div>

                                        {/* Inline note editor */}
                                        {isEditing && (
                                            <div className="flex flex-col gap-2">
                                                <textarea
                                                    autoFocus
                                                    value={noteDraft}
                                                    onChange={(e) => setNoteDraft(e.target.value)}
                                                    dir="rtl"
                                                    rows={3}
                                                    placeholder="Add notes for this diagnosis..."
                                                    className="w-full px-3 py-2 border border-blue-500 rounded bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveNote(code)}
                                                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleCancelEditNote}
                                                        className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Search input */}
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search diagnosis codes..."
                            value={diagnosisSearch}
                            onChange={(e) => setDiagnosisSearch(e.target.value)}
                            onFocus={() => setShowDiagnosisDropdown(true)}
                            className="w-full px-3 py-2 border border-[#B4D9D4] rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />

                        {/* Dropdown */}
                        {showDiagnosisDropdown && diagnosisSearch && (
                            <>
                                {/* Backdrop to close dropdown */}
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => {
                                        setShowDiagnosisDropdown(false);
                                        setDiagnosisSearch('');
                                    }}
                                />

                                {/* Dropdown menu */}
                                <div className="absolute z-20 w-full mt-1 bg-white border border-[#B4D9D4] rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                    {getFilteredDiagnosisCodes().length > 0 ? (
                                        getFilteredDiagnosisCodes().map((diagnosisItem) => (
                                            <div
                                                key={diagnosisItem.code}
                                                onClick={() => handleAddDiagnosis(diagnosisItem.code)}
                                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                                            >
                                                <div className="text-sm font-medium text-gray-900">
                                                    {diagnosisItem.code}
                                                </div>
                                                <div className="text-xs text-gray-600">
                                                    {diagnosisItem.Diagnosis}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-3 py-2 text-sm text-gray-500">
                                            No diagnosis codes found
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                </div>

                {/* Status */}
                <div className="p-4">
                    <label className="block text-xs font-medium text-gray-700 mb-2" dir="rtl">
                        סטטוס
                    </label>
                    <textarea
                        ref={statusRef}
                        value={formData.status}
                        onChange={(e) => {
                            handleFormChange('status', e.target.value);
                            expandTextarea(e.target as HTMLTextAreaElement);
                        }}
                        className="w-full px-3 py-2 rounded border border-[#B4D9D4] bg-[#B5D9D4] text-[#366A75] focus:outline-none focus:ring-2 focus:ring-[#366A75] text-sm overflow-y-hidden"
                        style={{ resize: 'none', height: '80px' }}
                        dir="rtl"
                    />
                </div>

                {/* Medications Section - Reusable Component */}
                {clinicId !== 24 && (
                    <MedicationSection
                        medications={medications}
                        setMedications={setMedications}
                        medicationTypes={medicationTypes}
                        medicationsEnabled={medicationsEnabled}
                        setMedicationsEnabled={setMedicationsEnabled}
                        onMedicationsChange={() => setHasUnsavedChanges(true)}
                        showToggle={true}
                        clinicId={clinicId}
                    />
                )}

                {/* Recommendations */}
                <div className="p-4">
                    <label className="block text-xs font-medium text-gray-700 mb-2" dir="rtl">
                        המלצות
                    </label>
                    <textarea
                        ref={recommendationsRef}
                        value={formData.recommendations}
                        onChange={(e) => {
                            handleFormChange('recommendations', e.target.value);
                            expandTextarea(e.target as HTMLTextAreaElement);
                        }}
                        className="w-full px-3 py-2 rounded border border-[#B4D9D4] bg-[#B5D9D4] text-[#366A75] focus:outline-none focus:ring-2 focus:ring-[#366A75] text-sm overflow-y-hidden"
                        style={{ resize: 'none', height: '80px' }}
                        dir="rtl"
                    />
                </div>
            </div>
        );
    }

    // Render Notes Tab
    function renderNotesTab() {
        if (!summary) return null;

        return (
            <div className="space-y-0">
                {/* סיכום ודיון (Summary and Discussion) Section */}
                <div className="overflow-y-hidden p-0 m-0">
                    <div className="px-4 py-1 bg-white flex items-center justify-start gap-2" dir="rtl">
                        <h3 className="text-sm font-bold text-gray-900">סיכום ודיון</h3>
                        <button
                            onClick={() => handleCopyToClipboard(summary.summary_and_discusion || '')}
                            className="text-gray-700 hover:text-gray-900"
                            title="Copy to clipboard"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                            </svg>
                        </button>
                    </div>
                    <textarea
                        ref={summaryTextareaRef}
                        value={summary.summary_and_discusion || ''}
                        readOnly
                        placeholder="No data available"
                        className="w-full border-0 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-[#B5D9D4] text-[#366A75] block overflow-y-hidden"
                        style={{ margin: 0, padding: '12px', display: 'block', resize: 'none' }}
                        dir="rtl"
                    />
                </div>

                {/* המלצות (Recommendations) Section */}
                <div className="overflow-y-hidden p-0 m-0">
                    <div className="px-4 py-1 bg-white flex items-center justify-start gap-2" dir="rtl">
                        <h3 className="text-sm font-bold text-gray-900">המלצות</h3>
                        <button
                            onClick={() => handleCopyToClipboard(summary.recommandations || '')}
                            className="text-gray-700 hover:text-gray-900"
                            title="Copy to clipboard"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                            </svg>
                        </button>
                    </div>
                    <textarea
                        ref={recommendationsTextareaRef}
                        value={summary.recommandations || ''}
                        readOnly
                        placeholder="No data available"
                        className="w-full border-0 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-[#B5D9D4] text-[#366A75] block overflow-y-hidden"
                        style={{ resize: 'none', margin: 0, padding: '12px', display: 'block' }}
                        dir="rtl"
                    />
                </div>
            </div>
        );
    }
};
